import dayjs from 'dayjs'

const isEmpty = (value: unknown): boolean => value === null || value === undefined || value === ''

/** Рядок дати від API: 2026-06-17T10:02:32+03:00, 2026-06-17 10:02, 2026-06-17 */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})?/

/**
 * Нормалізує значення поля до порівнюваного вигляду: порожні → null, дати (dayjs, Date,
 * ISO-рядок) → ISO в UTC, зв'язки з `@id` → сам `@id`, об'єкти — з відсортованими ключами.
 * Невалідна дата (недовведена) → null, щоб порівняння не кидало виняток.
 */
export const normalizeFormValue = (value: unknown): unknown => {
    if (isEmpty(value)) return null
    if (dayjs.isDayjs(value)) return value.isValid() ? value.toISOString() : null
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString()
    if (typeof value === 'string' && ISO_DATE_RE.test(value)) {
        const date = dayjs(value)
        return date.isValid() ? date.toISOString() : value
    }
    if (Array.isArray(value)) return value.map(normalizeFormValue)
    if (typeof value === 'object') {
        const obj = value as Record<string, unknown>
        if (typeof obj['@id'] === 'string') return obj['@id']
        return Object.fromEntries(
            Object.keys(obj)
                .sort()
                .map((key) => [key, normalizeFormValue(obj[key])])
        )
    }
    return value
}

/** Порожнє й `false` рівні: перемикач, якого не чіпали, може записати false замість null */
const isSameValue = (a: unknown, b: unknown): boolean => {
    if (a === b) return true
    if ((a === null && b === false) || (a === false && b === null)) return true
    return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Чи змінилися значення полів `fieldNames` відносно початкових.
 * Порівнюються лише названі поля: службові (`updatedAt`, `_permissions`…) не рахуються.
 * Правки всередині вкладених сутностей, згорнутих до `@id`, не видно — це свідоме спрощення.
 */
export const isFormDirty = (
    values: Record<string, unknown> | null | undefined,
    initialValues: Record<string, unknown> | null | undefined,
    fieldNames: string[]
): boolean =>
    fieldNames.some(
        (name) => !isSameValue(normalizeFormValue(values?.[name]), normalizeFormValue(initialValues?.[name]))
    )
